import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Eye, Filter } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import useAppraisalRole, { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const STATUS_OPTIONS = [
    "submitted_to_rm",
    "sent_back_by_rm",
    "rm_reviewed",
    "pending_hr_approval",
    "hr_approved",
    "published",
    "overdue",
].map((s) => ({ value: s, label: s.replace(/_/g, " ") }));

const TeamReviewList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { isRM } = useAppraisalRole();
    const { canAssignAppraisal } = useAppraisalPermissions();

    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const loadData = useCallback(async () => {
        if (!isRM) { setLoading(false); return; }
        setLoading(true);
        try {
            const filters = { scope: "team" };
            if (statusFilter) filters.status = statusFilter;
            const res = await fetch(APPRAISALS, filters);
            setTeam(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load team appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, isRM]);

    useEffect(() => { loadData(); }, [loadData]);

    if (!isRM) {
        return (
            <ContainerCard>
                <ModuleTitleCard Icon={Users} title="Team Review" subtitle="Only available to Reporting Managers" />
                <div className="text-center py-12 text-muted-foreground">
                    You don't have any subordinates configured. Contact HR if you should.
                </div>
            </ContainerCard>
        );
    }

    const filtered = team.filter((a) => {
        if (!search) return true;
        return (a.employee?.name || "").toLowerCase().includes(search.toLowerCase());
    });

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Users}
                title="Team Review"
                subtitle="Appraisals from your direct reports"
                totalRecords={filtered.length}
                buttonEnable={canAssignAppraisal}
                buttonName="Assign Appraisal"
                setOpenDialog={() => navigate("/appraisal/team-review/assign")}
            />

            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by employee"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <Select
                    placeholder="Status"
                    allowClear
                    value={statusFilter || undefined}
                    onChange={(v) => setStatusFilter(v || "")}
                    options={STATUS_OPTIONS}
                    style={{ minWidth: 200 }}
                />
            </div>

            <div className="px-3 pb-6 overflow-auto">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">No team appraisals to review.</div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">Employee</th>
                                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Cycle</th>
                                    <th className="px-3 py-2 font-medium hidden md:table-cell">Submitted</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((a) => (
                                    <tr key={a.id} className="border-t border-border hover:bg-muted/20">
                                        <td className="px-3 py-2">
                                            <button
                                                className="font-medium hover:text-sidebar-primary text-left"
                                                onClick={() => navigate(`/appraisal/team-review/${a.id}`)}
                                            >
                                                {a.employee?.name || "—"}
                                            </button>
                                            <p className="text-xs text-muted-foreground">{a.employee?.designation}</p>
                                        </td>
                                        <td className="px-3 py-2 hidden sm:table-cell">{a.cycle_name || "—"}</td>
                                        <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">
                                            {a.self_submitted_at ? dayjs(a.self_submitted_at).format("DD MMM YYYY") : "—"}
                                        </td>
                                        <td className="px-3 py-2"><StatusPill value={a.status} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/appraisal/team-review/${a.id}`)}
                                            >
                                                <Eye className="w-4 h-4 mr-1" /> Review
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default TeamReviewList;
